import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { cn } from '../lib/utils';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { 
  Ruler, Calculator, Minus, Plus, Check,
  Apple, Dribbble, Table2, ToggleLeft, ToggleRight
} from 'lucide-react';

// Preset quick sizes with icons
const PRESET_SIZES = [
  { 
    id: 'small', 
    areaM2: 0.005, 
    areaCm2: 50,
    labelHe: 'קטן', 
    labelEn: 'Small',
    icon: Apple,
    exampleHe: 'כמו תפוח',
    exampleEn: 'Like an apple'
  },
  { 
    id: 'medium', 
    areaM2: 0.18, 
    areaCm2: 1800,
    labelHe: 'בינוני', 
    labelEn: 'Medium',
    icon: Dribbble,
    exampleHe: 'כמו כדורסל',
    exampleEn: 'Like a basketball'
  },
  { 
    id: 'large', 
    areaM2: 1.2, 
    areaCm2: 12000,
    labelHe: 'גדול', 
    labelEn: 'Large',
    icon: Table2,
    exampleHe: 'כמו שולחן קפה',
    exampleEn: 'Like a coffee table'
  },
];

export const SizeSelector = ({ selectedSize, onSelect, customArea, onCustomAreaChange }) => {
  const { t, isRTL } = useLanguage();
  const [areaM2, setAreaM2] = useState(1);
  const [useCm2, setUseCm2] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [lengthM, setLengthM] = useState('1');
  const [widthM, setWidthM] = useState('1');
  const [showDimensions, setShowDimensions] = useState(false);
  const [inputValue, setInputValue] = useState('1');
  const [isEditingInput, setIsEditingInput] = useState(false);
  const sliderRef = useRef(null);

  // Max values - unlimited for input, slider shows up to 50 m²
  const SLIDER_MAX_M2 = 50;
  const SLIDER_MAX_CM2 = 500000;

  // Derived values
  const areaCm2 = areaM2 * 10000;
  const displayValue = useCm2 ? areaCm2 : areaM2;

  // Initialize
  useEffect(() => {
    if (!selectedSize) {
      const customSize = { id: 'custom', areaM2: 1, nameHe: 'מותאם אישית', nameEn: 'Custom' };
      onSelect(customSize);
      onCustomAreaChange(1);
    }
  }, []);

  useEffect(() => {
    if (customArea > 0 && customArea !== areaM2) {
      setAreaM2(customArea);
      setInputValue(customArea.toString());
    }
  }, [customArea]);

  const updateArea = (newAreaM2, updateDimensions = true) => {
    const clampedArea = Math.max(0.0001, newAreaM2);
    setAreaM2(clampedArea);
    setSelectedPreset(null);
    setInputValue(clampedArea >= 1 ? clampedArea.toFixed(1) : clampedArea.toFixed(4));
    const customSize = { id: 'custom', areaM2: clampedArea, nameHe: 'מותאם אישית', nameEn: 'Custom' };
    onSelect(customSize);
    onCustomAreaChange(clampedArea);
    
    // Only update dimension fields if requested (not when dimensions are the source)
    if (updateDimensions) {
      const side = Math.sqrt(clampedArea);
      setLengthM(side.toFixed(2));
      setWidthM(side.toFixed(2));
    }
  };

  const handlePresetSelect = (preset) => {
    setSelectedPreset(preset.id);
    setAreaM2(preset.areaM2);
    setInputValue(preset.areaM2.toString());
    const customSize = { id: preset.id, areaM2: preset.areaM2, nameHe: preset.labelHe, nameEn: preset.labelEn };
    onSelect(customSize);
    onCustomAreaChange(preset.areaM2);
    
    const side = Math.sqrt(preset.areaM2);
    setLengthM(side.toFixed(2));
    setWidthM(side.toFixed(2));
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleInputBlur = () => {
    setIsEditingInput(false);
    const parsed = parseFloat(inputValue.replace(/,/g, ''));
    if (!isNaN(parsed) && parsed > 0) {
      const newAreaM2 = useCm2 ? parsed / 10000 : parsed;
      updateArea(newAreaM2, true);
    } else {
      setInputValue(useCm2 ? Math.round(areaCm2).toString() : areaM2.toString());
    }
  };

  const handleInputFocus = () => {
    setIsEditingInput(true);
    setInputValue(useCm2 ? Math.round(areaCm2).toString() : (areaM2 >= 1 ? areaM2.toFixed(1) : areaM2.toFixed(4)));
  };

  const handleDimensionChange = (setter, value) => {
    setter(value);
  };

  const calculateFromDimensions = () => {
    const length = parseFloat(lengthM) || 0;
    const width = parseFloat(widthM) || 0;
    const newAreaM2 = length * width;
    if (newAreaM2 > 0) {
      // Don't update dimensions back - they are the source
      const clampedArea = Math.max(0.0001, newAreaM2);
      setAreaM2(clampedArea);
      setSelectedPreset(null);
      setInputValue(clampedArea >= 1 ? clampedArea.toFixed(1) : clampedArea.toFixed(4));
      const customSize = { id: 'custom', areaM2: clampedArea, nameHe: 'מותאם אישית', nameEn: 'Custom' };
      onSelect(customSize);
      onCustomAreaChange(clampedArea);
    }
  };

  useEffect(() => {
    if (showDimensions) {
      calculateFromDimensions();
    }
  }, [lengthM, widthM, showDimensions]);

  const increment = () => {
    let step;
    if (areaM2 < 0.1) step = 0.01;
    else if (areaM2 < 1) step = 0.1;
    else if (areaM2 < 10) step = 0.5;
    else step = 1;
    
    updateArea(areaM2 + step);
  };

  const decrement = () => {
    let step;
    if (areaM2 <= 0.1) step = 0.01;
    else if (areaM2 <= 1) step = 0.1;
    else if (areaM2 <= 10) step = 0.5;
    else step = 1;
    
    updateArea(Math.max(0.0001, areaM2 - step));
  };

  const toggleUnit = () => {
    setUseCm2(!useCm2);
    setInputValue(!useCm2 ? Math.round(areaCm2).toString() : (areaM2 >= 1 ? areaM2.toFixed(1) : areaM2.toFixed(4)));
  };

  // Slider position (0-100) - capped at slider max
  const sliderPosition = useCm2 
    ? Math.min(100, (areaCm2 / SLIDER_MAX_CM2) * 100)
    : Math.min(100, (areaM2 / SLIDER_MAX_M2) * 100);

  // Touch/Mouse handlers for slider
  const handleSliderInteraction = (e) => {
    if (!sliderRef.current) return;
    
    const rect = sliderRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const percent = x / rect.width;
    
    const maxVal = useCm2 ? SLIDER_MAX_CM2 : SLIDER_MAX_M2;
    const newValue = Math.max(useCm2 ? 1 : 0.0001, percent * maxVal);
    const newAreaM2 = useCm2 ? newValue / 10000 : newValue;
    updateArea(newAreaM2);
  };

  const [isDragging, setIsDragging] = useState(false);

  const handleSliderStart = (e) => {
    setIsDragging(true);
    handleSliderInteraction(e);
  };

  const handleSliderMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    handleSliderInteraction(e);
  };

  const handleSliderEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      const handleMove = (e) => handleSliderMove(e);
      const handleEnd = () => handleSliderEnd();
      
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleEnd);
      window.addEventListener('touchmove', handleMove, { passive: false });
      window.addEventListener('touchend', handleEnd);
      
      return () => {
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('mouseup', handleEnd);
        window.removeEventListener('touchmove', handleMove);
        window.removeEventListener('touchend', handleEnd);
      };
    }
  }, [isDragging, useCm2]);

  // Format for display
  const formatDisplay = (val) => {
    if (useCm2) {
      return Math.round(val).toLocaleString();
    }
    if (val >= 100) return Math.round(val).toLocaleString();
    if (val >= 1) return val.toFixed(1);
    if (val >= 0.01) return val.toFixed(2);
    return val.toFixed(4);
  };

  return (
    <div className="space-y-4">
      {/* Preset Size Cards */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        {PRESET_SIZES.map((preset, index) => {
          const isActive = selectedPreset === preset.id;
          const IconComponent = preset.icon;
          
          return (
            <motion.button
              key={preset.id}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.08 }}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => handlePresetSelect(preset)}
              data-testid={`preset-size-${preset.id}`}
              className={cn(
                'relative p-3 sm:p-4 rounded-xl border-2 transition-all duration-200 text-center group',
                isActive
                  ? 'border-accent bg-accent/10 shadow-md shadow-accent/20'
                  : 'border-border/40 hover:border-accent/40 bg-card hover:bg-secondary/30'
              )}
            >
              {isActive && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-1.5 -end-1.5 w-5 h-5 rounded-full bg-accent flex items-center justify-center shadow-md z-10"
                >
                  <Check className="w-3 h-3 text-accent-foreground" />
                </motion.div>
              )}

              <div className="flex flex-col items-center gap-1.5">
                <div className={cn(
                  'w-10 h-10 sm:w-11 sm:h-11 rounded-lg flex items-center justify-center transition-all',
                  isActive
                    ? 'bg-accent text-accent-foreground'
                    : 'bg-secondary/80 text-muted-foreground group-hover:text-accent'
                )}>
                  <IconComponent className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>

                <span className={cn(
                  'text-sm sm:text-base font-semibold',
                  isActive ? 'text-accent' : 'text-foreground'
                )}>
                  {isRTL ? preset.labelHe : preset.labelEn}
                </span>

                <span className="text-[10px] sm:text-xs text-muted-foreground">
                  {useCm2 
                    ? `${preset.areaCm2.toLocaleString()} סמ"ר`
                    : `${preset.areaM2} מ"ר`
                  }
                </span>
              </div>
            </motion.button>
          );
        })}
      </div>

      {/* Custom Size Control */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative rounded-xl border border-border/50 bg-gradient-to-br from-card to-secondary/20 p-4 sm:p-5"
      >
        {/* Unit Toggle */}
        <div className="flex justify-center mb-4">
          <button
            onClick={toggleUnit}
            className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary/50 hover:bg-secondary transition-colors text-sm"
            data-testid="unit-toggle"
          >
            <span className={cn('transition-colors', !useCm2 ? 'text-accent font-semibold' : 'text-muted-foreground')}>
              מ"ר
            </span>
            {useCm2 ? (
              <ToggleRight className="w-5 h-5 text-accent" />
            ) : (
              <ToggleLeft className="w-5 h-5 text-muted-foreground" />
            )}
            <span className={cn('transition-colors', useCm2 ? 'text-accent font-semibold' : 'text-muted-foreground')}>
              סמ"ר
            </span>
          </button>
        </div>

        {/* Area Value with +/- controls - LTR layout for number progression */}
        <div className="flex items-center justify-center gap-3 mb-5" dir="ltr">
          {/* Minus on LEFT (decreases value) */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={decrement}
            className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-secondary hover:bg-secondary/80 border border-border/50 flex items-center justify-center text-foreground transition-all"
            data-testid="size-decrement-btn"
          >
            <Minus className="w-5 h-5" />
          </motion.button>
          
          {/* Editable Input */}
          <div className="flex flex-col items-center min-w-[140px]">
            <div className="flex items-baseline gap-1">
              <input
                type="text"
                inputMode="decimal"
                value={isEditingInput ? inputValue : formatDisplay(displayValue)}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                className="w-28 text-3xl sm:text-4xl font-bold text-foreground text-center bg-transparent hover:bg-secondary/30 focus:bg-secondary/50 rounded-lg border-2 border-transparent focus:border-accent outline-none px-2 py-1 transition-all"
                data-testid="size-input"
              />
              <span className="text-base sm:text-lg text-muted-foreground font-medium">
                {useCm2 ? 'סמ"ר' : 'מ"ר'}
              </span>
            </div>
            {!useCm2 && areaM2 < 1 && (
              <span className="text-xs text-muted-foreground">
                = {Math.round(areaCm2).toLocaleString()} סמ"ר
              </span>
            )}
            {useCm2 && areaCm2 >= 10000 && (
              <span className="text-xs text-muted-foreground">
                = {areaM2.toFixed(2)} מ"ר
              </span>
            )}
          </div>
          
          {/* Plus on RIGHT (increases value) */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={increment}
            className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-secondary hover:bg-secondary/80 border border-border/50 flex items-center justify-center text-foreground transition-all"
            data-testid="size-increment-btn"
          >
            <Plus className="w-5 h-5" />
          </motion.button>
        </div>

        {/* Slider - Touch enabled, LTR */}
        <div 
          ref={sliderRef}
          className="relative h-10 mb-3 cursor-pointer select-none" 
          dir="ltr"
          onMouseDown={handleSliderStart}
          onTouchStart={handleSliderStart}
        >
          {/* Track Background */}
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-2 rounded-full bg-gradient-to-r from-secondary via-secondary to-secondary/80 border border-border/30 shadow-inner" />
          
          {/* Filled Track */}
          <motion.div
            className="absolute top-1/2 -translate-y-1/2 h-2 rounded-full bg-gradient-to-r from-accent to-accent/80 shadow-sm"
            style={{ width: `${sliderPosition}%`, left: 0 }}
            initial={false}
            animate={{ width: `${sliderPosition}%` }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          />

          {/* Thumb */}
          <motion.div
            className="absolute top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-accent shadow-lg border-[3px] border-white cursor-grab active:cursor-grabbing"
            style={{ left: `calc(${sliderPosition}% - 12px)` }}
            initial={false}
            animate={{ left: `calc(${sliderPosition}% - 12px)` }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            whileHover={{ scale: 1.15 }}
            whileTap={{ scale: 0.95 }}
          />
        </div>

        {/* Scale Labels */}
        <div className="flex justify-between text-[10px] sm:text-xs text-muted-foreground px-1" dir="ltr">
          <span>0</span>
          <span>{useCm2 ? '100K' : '10'}</span>
          <span>{useCm2 ? '200K' : '20'}</span>
          <span>{useCm2 ? '300K' : '30'}</span>
          <span>{useCm2 ? '400K' : '40'}</span>
          <span>{useCm2 ? '500K סמ"ר' : '50 מ"ר'}</span>
        </div>

        {/* Quick Values */}
        <div className="flex justify-center gap-1.5 mt-4 flex-wrap">
          {(useCm2 ? [100, 1000, 5000, 10000, 50000] : [0.5, 1, 5, 10, 25]).map((value) => {
            const isActive = useCm2 
              ? Math.abs(areaCm2 - value) < 10 
              : Math.abs(areaM2 - value) < 0.01;
            return (
              <motion.button
                key={value}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  const newAreaM2 = useCm2 ? value / 10000 : value;
                  updateArea(newAreaM2);
                }}
                className={cn(
                  'px-2.5 py-1 rounded-md text-xs font-medium transition-all',
                  isActive
                    ? 'bg-accent text-accent-foreground'
                    : 'bg-secondary/50 text-muted-foreground hover:bg-secondary hover:text-foreground'
                )}
              >
                {value.toLocaleString()}
              </motion.button>
            );
          })}
        </div>
      </motion.div>

      {/* Dimensions Toggle */}
      <div className="text-center">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowDimensions(!showDimensions)}
          className="text-muted-foreground text-xs h-8"
        >
          <Ruler className="w-3.5 h-3.5 me-1.5" />
          {isRTL ? 'חישוב לפי מידות' : 'Calculate by dimensions'}
        </Button>
      </div>

      {/* Dimensions Input */}
      <AnimatePresence>
        {showDimensions && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="rounded-xl border border-border/50 bg-card p-4 overflow-hidden"
          >
            <div className="flex items-center gap-2 mb-3">
              <Calculator className="w-4 h-4 text-accent" />
              <span className="font-medium text-sm">{isRTL ? 'חישוב לפי מטרים' : 'Calculate by meters'}</span>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">{isRTL ? 'אורך' : 'Length'}</label>
                <div className="flex items-center gap-1.5">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={lengthM}
                    onChange={(e) => handleDimensionChange(setLengthM, e.target.value)}
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    placeholder="0.00"
                  />
                  <span className="text-xs text-muted-foreground whitespace-nowrap">מ'</span>
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">{isRTL ? 'רוחב' : 'Width'}</label>
                <div className="flex items-center gap-1.5">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={widthM}
                    onChange={(e) => handleDimensionChange(setWidthM, e.target.value)}
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    placeholder="0.00"
                  />
                  <span className="text-xs text-muted-foreground whitespace-nowrap">מ'</span>
                </div>
              </div>
            </div>
            
            <div className="mt-3 p-2 rounded-lg bg-secondary/50 text-center">
              <span className="text-xs text-muted-foreground">
                {lengthM || '0'} × {widthM || '0'} = 
              </span>
              <span className="text-sm font-bold text-accent ms-1">
                {areaM2.toFixed(2)} מ"ר
              </span>
              <span className="text-xs text-muted-foreground ms-2">
                ({Math.round(areaCm2).toLocaleString()} סמ"ר)
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
